import { Component } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BaseCtl, ServiceLocatorService } from '@ncs/ncs-lib';

@Component({
  selector: 'app-application-info',
  templateUrl: './application-info.component.html',
  styleUrls: ['./application-info.component.scss'],
})
export class ApplicationInfoComponent extends BaseCtl {
  // Application related properties
  public selectApp: any = undefined;
  public applicationRoles: any = [];

  // Image upload and preview properties
  public maxFileSize = 512000; // 0.5 MB
  public errorMessage: string | null = null;

  public appId: any;
  // Validation flags
  appUrlInvalid = false;
  appname = false;

  constructor(
    public override locator: ServiceLocatorService,
    public override route: ActivatedRoute,
  ) {
    super(locator.config.get('endpoint.application'), locator, route);

    locator.config
      .getProperty('endpoint.application')
      .then((property: string) => {
        this.initApiEndpoint(property);
      });
    
     locator.config
      .getProperty('SUBDOMAIN')
      .then((property: string) => {
       this.BASE_DOMAIN=property;
      });

    let _self = this;
    if (route.snapshot.paramMap.get('id'))
      _self.appId = Number(route.snapshot.paramMap.get('id'));
    _self.form.searchParams['appId'] = _self.appId;
    _self.search();
    _self.get(_self.appId);
  }

  override ngOnInit() {
    // Fetch User App Server URL if required
    this.locator.config
      .getProperty('USER_APP_SERVER_URL')
      .then((property: string) => {
        this.preload();
        this.search();
      });
  }

  override populateForm(form: any, data: any) {
    form.id = data.id;
    form.name = data.name;
    form.appUrl = data.appUrl;
    form.userName = data.userName;
    form.userId = data.userId;
    form.orgName = data.orgName;
    form.status = data.status;
    form.logo = data.logo;
    form.code = data.code;
    form.enableUserSignup = data.enableUserSignup;
    form.roleId = data.roleId;
    form.domainUrl = data.domainUrl;
    form.landingPage = data.landingPage;

    form.loginByEmail = data.loginByEmail;
    form.loginByMobileNo = data.loginByMobileNo;
    form.loginByLoginCode = data.loginByLoginCode;
    form.showLandingPageUpload = data.showLandingPageUpload;

    if (data.appUrl) {
      const sub = data.appUrl
        .replace('https://', '')
        .replace(`.${this.BASE_DOMAIN}`, '');
      form.subdomain = sub;
      this.subdomainManuallyChanged = true;
    }
  }

  /**
   * Show Preview of the selected image file
   */
  showPreview(event: any, code: string): void {
    const file = event.target?.files?.[0];

    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      this.imagePreview[code] = reader.result as string;
    };
    reader.readAsDataURL(file);
  }

  /**
   * Get document thumbnail image
   */
  getDocThumbnailImage(logo: any) {
    return this.getDocThumbnail(logo);
  }

  /**
   * Handle file selection and perform size validation
   */

  selectedFileCodeFile: { [code: string]: File } = {};
  selectedImageIdMap: { [code: string]: number } = {};
  imagePreview: { [key: string]: string | null } = {};

  public onFileChanged(event: any, code: string, imageId: any): void {
    const fileObj = event.target.files[0];
    if (!fileObj) return;

    const fileType = fileObj.type.toLowerCase();
    const fileName = fileObj.name.toLowerCase();
    const fileExtension = fileName.substring(fileName.lastIndexOf('.') + 1);

    const disallowedMimeTypes = ['image/tiff', 'image/x-tiff'];
    const disallowedExtensions = ['tif', 'tiff'];

    if (
      disallowedMimeTypes.includes(fileType) ||
      disallowedExtensions.includes(fileExtension)
    ) {
      this.errorMessage = 'TIFF images are not allowed.';
      return;
    }

    if (fileObj.size > this.maxFileSize) {
      this.errorMessage = 'File size should not exceed 0.5 MB.';
      return;
    }

    this.errorMessage = null;
    this.showPreview(event, code); // Optional: image preview

    this.selectedFileCodeFile[code] = fileObj;
    this.selectedImageIdMap[code] = imageId ?? 0;
  }

  public uploadLandingpage(event: any, code: string, imageId: any): void {
    const fileObj = event.target.files[0];
    if (!fileObj) return;

    this.errorMessage = null;
    this.showPreview(event, code);
    this.selectedFileCodeFile[code] = fileObj;
    this.selectedImageIdMap[code] = imageId ?? 0;
  }

  /**
   * Validate App URL - only alphanumeric and single space allowed
   */
  validateString(event: Event): void {
    const input = event.target as HTMLInputElement;
    let value = input.value;
    const regex = /^[a-zA-Z0-9]+( [a-zA-Z0-9]+)?$/;

    this.appUrlInvalid = !regex.test(value);

    if (this.appUrlInvalid) {
      this.form.inputerror['appUrl'] = 'Special characters are not allowed!';
      this.form.data['appUrl'] = this.form.data['appUrl'].replace(
        /[^a-zA-Z0-9 ]*/g,
        '',
      );
      this.appUrlInvalid = false;
      this.form.inputerror['appUrl'] = null;
    } else {
      this.form.inputerror['appUrl'] = null;
    }
  }

  /**
   * Validate Application Name (Required and Length)
   */
  checkValue(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = this.normalizeSpaces(input.value.trim());

    this.form.data['name'] = value === '' ? null : this.capitalizeWords(value);

    if (!this.form.data['name']) {
      this.form.data['name'] = undefined;
      this.form.inputerror['name'] = 'Name is required';
      this.appname = true;
    } else if (this.form.data['name'].length > 40) {
      this.form.inputerror['name'] = 'Application Name is too long.';
      this.appname = false;
    } else {
      this.appname = false;
      this.form.inputerror['name'] = null;
    }

    if (!this.subdomainManuallyChanged && this.form.data['name']) {
      const generated = this.generateSubdomainFromName(this.form.data['name']);
      this.form.data['subdomain'] = generated;
      this.form.data['appUrl'] = `https://${generated}.${this.BASE_DOMAIN}`;
      alert(this.form.data['appUrl'] )
    }
  }

  private generateSubdomainFromName(name: string): string {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9 ]/g, '') // remove special chars
      .replace(/\s+/g, '-') // spaces to hyphen
      .replace(/^-+|-+$/g, '') // trim hyphens
      .substring(0, 63); // DNS safe length
  }

  /**
   * Normalize multiple spaces to single space
   */
  normalizeSpaces(str: string): string {
    return str.replace(/\s+/g, ' ');
  }

  /**
   * Capitalize first letter of each word unless all uppercase
   */
  capitalizeWords(str: string): string {
    return str
      .split(' ')
      .map((word) =>
        word === word.toUpperCase()
          ? word
          : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
      )
      .join(' ');
  }

  /**
   * Retrieve application info by ID
   */
  async retrieve(id: any, callback?: any) {
    var _self = this;
    _self.form.error = false;
    _self.form.message = '';
    _self.form.isConfirmDelete = true;

    if (id && id > 0) {
      if (_self.form.isConfirmDelete) {
        let flag = confirm('Are you sure ?');
        if (!flag) return;

        let baseUrl = await this.locator.config.getProperty(
          'endpoint.application',
        );
        let url = baseUrl + '/retrieve/' + id;

        this.locator.http.get(url, function (res: any, error: any) {
          if (error) return;

          _self.form.error = !res.success;
          _self.form.message = res.result.message;

          if (res.success) {
            _self.form.message = 'Data is retrieved';
            if (callback) callback(res, error);
            _self.search();
          }
        });
      }
    }
  }

  /**
   * Override BaseCtl get method to fetch application roles
   */
  override get(id: number, callback?: any): void {
    this.getApplicationRoles(id);

    super.get(id, () => {
      this.checkDefaultLogin();
      if (callback) callback();
    });
  }

  /**
   * Get Application Roles for a specific Application ID
   */
  getApplicationRoles(id: any) {
    let _self = this;
    if (id === 0) return;

    let url =
      this.locator.config.get('endpoint.application.users') +
      '/application-roles/' +
      id;

    this.locator.http.get(url, function (res: any, error: boolean) {
      if (error) {
        alert('Server Error:' + error);
        return;
      }
      _self.applicationRoles = res.result.data;
    });
  }

  /**
   * Truncate text to a specified limit and append '...'
   */
  truncate(text: string, limit: number): string {
    if (!text) return '';
    return text.length > limit ? text.substring(0, limit) + '...' : text;
  }

  uploadFileToServer(file: File, code: string, imageId: any): Promise<any> {
    if (imageId === null || imageId === undefined) {
      imageId = 0;
    }

    const url =
      this.locator.getEndpoint('endpoint.api.v1') +
      '/cuickdev/application/doc/' +
      this.form.data['id'] +
      '/' +
      imageId +
      '/' +
      code;
    const param = { id: this.form.data.id };

    return new Promise((resolve, reject) => {
      this.locator.http.postMutipart(
        url,
        param,
        file,
        (res: any, error: any) => {
          if (error) {
            reject(error);
          } else {
            this.form.error = !res.success;
            this.form.message = res.result.message;
            if (res.success) {
              this.form.message = 'File is uploaded';
              this.form.data[code] = res.result.data[code];
              this.changeDoc();
              this.getDoc(this.form.data[code]);
            }
            resolve(res);
          }
        },
      );
    });
  }

  uploadImagesThenSaveForm(Files: any): Promise<void> {
    const fileKeys = Object.keys(Files);

    const uploadPromises = fileKeys.map((code) => {
      const file = Files[code];
      return this.uploadFileToServer(file, code, 0);
    });

    return Promise.all(uploadPromises).then((responses) => {
      responses.forEach((response: any, index: number) => {
        const code = fileKeys[index];
        const value =
          response?.result?.data?.[code] || response?.result?.data?.logo;

        if (value) {
          this.form.data[code] = value;
        }
      });
    });
  }

  saveData() {


  const subdomain = this.form.data['subdomain']?.trim();
  const isValid = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/.test(subdomain);

  if (!isValid || !subdomain) {    
    this.form.inputerror['subdomain'] = 'Invalid URL! Please fix it before saving.';
    return; 
  }

  // ✅ Valid hai toh aage badho
  this.form.inputerror['subdomain'] = null;
    const fileEntries = Object.entries(this.selectedFileCodeFile);
    if (fileEntries.length > 0) {
      this.uploadImagesThenSaveForm(this.selectedFileCodeFile)
        .then(() => {
          // Clear stored file maps
          this.selectedFileCodeFile = {};
          this.selectedImageIdMap = {};

          this.form.message = 'All files uploaded successfully.';
          this.search();
          super.save();
        })
        .catch((error) => {
          alert('File upload failed: ' + error);
        });
    } else {
      super.save();
    }
  }

  /**
   * Reset Image Preview
   */
  imagePreviewReset() {
    this.imagePreview = {};
  }

  /**
   * Clear form list and perform search
   */
  search1() {
    this.form.list = [];
    this.search();
  }

  /**
   * Set default login by email if undefined
   */
  checkDefaultLogin() {
    if (this.form.data['loginByEmail'] === undefined) {
      this.form.data['loginByEmail'] = true;
    }
  }

  onLandingPageToggle() {
    if (!this.form.data['showLandingPageUpload']) {
      this.form.data['landingPage'] = null;
    }
  }

  /**
   * Remove the logo from preview and mark for backend update
   */
  removeLogo(): void {
    // Clear the preview
    this.imagePreview['logo'] = null;

    // Remove any newly selected file
    if (this.selectedFileCodeFile['logo']) {
      delete this.selectedFileCodeFile['logo'];
    }

    // Clear the existing logo reference so backend knows to remove it
    this.form.data['logo'] = 0;

    // Clear imageId mapping
    const imageId = this.selectedImageIdMap['logo'] ?? 0;
    if (this.selectedImageIdMap['logo']) {
      delete this.selectedImageIdMap['logo'];
    }

    this.saveData();
  }

  public BASE_DOMAIN :any;
  private subdomainManuallyChanged = false;

  onSubdomainChange(value: string): void {
    this.subdomainManuallyChanged = true;

    if (!value) {
      this.form.data['appUrl'] = null;
      return;
    }

    const cleaned = value.toLowerCase().trim();

    if (!this.isValidSubdomain(cleaned)) {
      this.form.inputerror['subdomain'] =
        'Only lowercase letters, numbers and hyphens allowed. Cannot start or end with hyphen.';
      return;
    }

    this.form.inputerror['subdomain'] = null;
    this.form.data['subdomain'] = cleaned;
    this.form.data['appUrl'] = `https://${cleaned}.${this.BASE_DOMAIN}`;
  }

  private isValidSubdomain(name: string): boolean {
    const regex = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/;
    return regex.test(name);
  }

copied = false;
private copyTimeout: any;
copyUrl(): void {
  const subdomain = this.form?.data?.['subdomain']?.trim();

  if (!subdomain) return;

  const fullUrl = `https://${subdomain}${this.BASE_DOMAIN}`;

  navigator.clipboard.writeText(fullUrl)
    .then(() => {

      // Clear old timer if user clicks repeatedly
      if (this.copyTimeout) {
        clearTimeout(this.copyTimeout);
      }

      this.copied = true;

      this.copyTimeout = setTimeout(() => {
        this.copied = false;
      }, 2000);

    })
    .catch((error) => {
      console.error('Copy failed:', error);
    });
}
}
